// ignore-string-externalization

import { ContentfulEntry, FaqArticle, FaqCategory } from './util';
import {
  loadCategory,
  loadFaq,
  loadHome,
  loadRelatedCategory,
} from './loaders';
import { HelpHomeTemplateType } from '../Template/Help/HelpHomeTemplate';

const hasUrlSlug = (categorySlug: string) => (category: FaqCategory) =>
  category.urlSlug === categorySlug;
const hasArticle = (articleSlug: string) => (category: FaqCategory) =>
  category.faqs.find((article: FaqArticle) => article.urlSlug === articleSlug);

const attachFaqCategory = (
  items: ContentfulEntry[],
  template: HelpHomeTemplateType,
  smartlingLanguage: string,
  preview: boolean,
) => {
  return Promise.all(
    items.map(item => {
      if (item.contentType !== 'faq') {
        return item;
      }
      const categoryFromHelpHome = template.faqCategories?.find(
        hasArticle(item.urlSlug),
      );
      return (
        categoryFromHelpHome
          ? Promise.resolve(categoryFromHelpHome)
          : loadRelatedCategory({
              key: item.id,
              preview,
              smartlingLanguage,
            })
      ).then(category => ({ ...item, category }));
    }),
  );
};

export const getHomePageData = async (
  preview: boolean,
  smartlingLanguage: string,
) => {
  const template = await loadHome({ preview, smartlingLanguage });

  return {
    template,
    preview,
  };
};

export const getArticlePageData = async (
  articleSlug: string,
  preview: boolean,
  smartlingLanguage: string,
  categorySlug?: string,
) => {
  const [template, faq] = await Promise.all([
    loadHome({ preview, smartlingLanguage }),
    loadFaq({
      key: articleSlug,
      preview,
      smartlingLanguage,
    }),
  ]);

  if (!faq || !template) {
    return {};
  }

  const categoryFromHelpHome = categorySlug
    ? template?.faqCategories?.find(hasUrlSlug(categorySlug))
    : template?.faqCategories?.find(hasArticle(faq.urlSlug));

  const [category, related] = await Promise.all([
    categoryFromHelpHome ||
      (categorySlug
        ? loadCategory({
            key: categorySlug as string,
            preview,
            smartlingLanguage,
          })
        : loadRelatedCategory({
            key: faq.id,
            preview,
            smartlingLanguage,
          })),
    attachFaqCategory(faq.related || [], template, smartlingLanguage, preview),
  ]);

  return {
    template,
    faq: {
      ...faq,
      related,
    },
    category,
    preview,
  };
};

export const getCategoryPageData = async (
  categorySlug: string,
  preview: boolean,
  smartlingLanguage: string,
) => {
  const [template, category] = await Promise.all([
    loadHome({ preview, smartlingLanguage }),
    loadCategory({
      key: categorySlug,
      preview,
      smartlingLanguage,
    }),
  ]);

  return {
    template,
    category,
    preview,
  };
};
